import React from 'react';
import { theme } from '../../theme';
import { displayProviderName, getProviderColor } from '../../utils/providerUtils';
import { useLanguage } from '../../i18n/useLanguage';
import { LanguageToggle } from '../LanguageToggle';
import type { Provider } from '../../types';

type ProviderSelectionStepProps = {
  providers: Provider[];
  selection: Record<string, boolean>;
  onToggle: (providerId: string) => void;
  onProceed: () => void;
  onClose: () => void;
};

export const ProviderSelectionStep: React.FC<ProviderSelectionStepProps> = ({
  providers,
  selection,
  onToggle,
  onProceed,
  onClose,
}) => {
  const { t } = useLanguage();
  const selectedCount = Object.values(selection).filter(Boolean).length;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'transparent',
        color: theme.text,
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Header */}
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2000,
          background: 'rgba(255,255,255,0.85)',
          borderBottom: '1px solid rgba(255,106,76,0.3)',
          backdropFilter: 'blur(6px)',
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
            }}
            onClick={onClose}
          >
            <img
              src="/sovlogo.png"
              alt="Go-Sovereign"
              className="logo-desktop"
              style={{
                height: 50,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
            <img
              src="/sovicon.png"
              alt="Go-Sovereign Icon"
              className="logo-mobile"
              style={{
                height: 36,
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LanguageToggle />
            <button
              onClick={onClose}
              style={{
                border: '1px solid #FF6A4C',
                background: 'transparent',
                color: '#FF6A4C',
                padding: '6px 14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('questionnaire.close')}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer */}
      <div style={{ height: 64 }} />

      {/* Content */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>
          {t('providers.title')}
        </h2>
        <p style={{ marginTop: 6, color: theme.muted }}>
          {t('providers.subtitle')}
        </p>

        {/* Provider Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 12,
            marginTop: 16,
          }}
        >
          {providers.map((prov) => {
            const isSelected = selection[prov.id];
            const color = getProviderColor(prov.name);

            return (
              <div
                key={prov.id}
                onClick={() => onToggle(prov.id)}
                style={{
                  padding: 16,
                  border: `2px solid ${isSelected ? color : theme.border}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: isSelected ? `${color}15` : 'rgba(255,255,255,0.9)',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  fontWeight: isSelected ? 700 : 500,
                }}
              >
                {displayProviderName(prov.name)}
              </div>
            );
          })}
        </div>

        {/* Proceed Button */}
        <button
          onClick={onProceed}
          disabled={selectedCount === 0}
          style={{
            marginTop: 20,
            padding: '10px 20px',
            border: `1px solid ${theme.primaryBorder}`,
            background: selectedCount > 0 ? theme.primarySoft : '#eee',
            color: theme.text,
            fontWeight: 700,
            cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
            borderRadius: 6,
          }}
        >
          {t('providers.proceed')} ({selectedCount} {t('providers.select').toLowerCase()})
        </button>
      </section>
    </div>
  );
};
